/**
 * Keeps the PDP star rating under the product title (blocks/review.liquid) in step with the
 * Emplifi (TurnTo) reviews widget further down the page (ECOMDEV-1151).
 *
 * The block is server-rendered from the `reviews.rating` / `reviews.rating_count` metafields, which
 * Emplifi syncs on its own schedule and has never written for some products (recent launches
 * especially). This reads the same summary the widget renders from and updates the stars and count,
 * revealing the block when the metafields were missing.
 *
 * It only fetches; it never touches `TurnToCmd`, which must not be called before the widget boots.
 */

const SITE_KEY = 'npE7gvJsqYWnwPLsite';
const XLINK_NS = 'http://www.w3.org/1999/xlink';

/** @type {Map<string, Promise<{ rating: number, count: number }>>} */
const summaries = new Map();

/**
 * Fetches (once per SKU per page) the rating and total ratings count Emplifi shows for a SKU.
 * @param {string} sku
 * @returns {Promise<{ rating: number, count: number }>}
 */
function fetchSummary(sku) {
  if (!summaries.has(sku)) {
    const url = `https://cdn-ws.turnto.com/v5/sitedata/${SITE_KEY}/${encodeURIComponent(sku)}/d/review/summary/en_US`;
    const request = fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((summary) => {
        const breakdownTotal = (summary.ratingsBreakdown || []).reduce(
          (total, row) => total + (row.num || 0),
          0
        );
        return {
          rating: Number(summary.rating) || 0,
          count: Number(summary.reviewsCount ?? breakdownTotal) || 0,
        };
      });
    request.catch(() => summaries.delete(sku));
    summaries.set(sku, request);
  }
  return /** @type {Promise<{ rating: number, count: number }>} */ (summaries.get(sku));
}

/**
 * Which sprite symbol each star shows, using the same rounding as blocks/review.liquid: a decimal
 * of .3–.7 is a half star (star-empty filled with the #star-half gradient), above .7 rounds up.
 * @param {number} rating
 * @param {number} starCount
 * @returns {Array<{ symbol: string, half: boolean }>}
 */
function getStarSymbols(rating, starCount) {
  const decimal = rating % 1;
  let decimalRating = 0;
  if (decimal >= 0.3 && decimal <= 0.7) decimalRating = 0.5;
  else if (decimal > 0.7) decimalRating = 1;
  const filledStars = Math.floor(rating) + Math.ceil(decimalRating);

  return Array.from({ length: starCount }, (_, index) => {
    const star = index + 1;
    if (star < filledStars || (star === filledStars && decimalRating !== 0.5)) {
      return { symbol: '#star', half: false };
    }
    return { symbol: '#star-empty', half: decimalRating === 0.5 && star === filledStars };
  });
}

class EmplifiRating extends HTMLElement {
  static observedAttributes = ['data-sku'];

  connectedCallback() {
    this.refresh();
  }

  attributeChangedCallback() {
    if (this.isConnected) this.refresh();
  }

  async refresh() {
    const sku = this.dataset.sku;
    if (!sku) return;

    let summary;
    try {
      summary = await fetchSummary(sku);
    } catch (e) {
      console.warn('[emplifi-rating] could not load the Emplifi summary', { sku, e });
      return;
    }

    // The block may have been re-rendered for another variant while the request was in flight.
    if (sku !== this.dataset.sku) return;
    this.render(summary);
  }

  /**
   * @param {{ rating: number, count: number }} summary
   */
  render({ rating, count }) {
    const link = this.querySelector('.rating-wrapper');
    if (!(link instanceof HTMLElement)) return;

    if (!count) {
      link.hidden = true;
      return;
    }

    const label = (this.dataset.ratingLabel || '').replace('[rating]', String(rating));
    const stars = this.querySelectorAll('.rating svg.stars');
    getStarSymbols(rating, stars.length).forEach(({ symbol, half }, index) => {
      const use = stars[index].querySelector('use');
      if (!use) return;
      use.setAttributeNS(XLINK_NS, 'xlink:href', symbol);
      if (half) use.setAttribute('fill', 'url(#star-half)');
      else use.removeAttribute('fill');
    });

    this.querySelector('.rating')?.setAttribute('aria-label', label);
    const labelText = this.querySelector('.rating-label');
    if (labelText) labelText.textContent = `${label} `;
    const countText = this.querySelector('.rating-count-value');
    if (countText) countText.textContent = String(count);

    link.hidden = false;
  }
}

if (!customElements.get('emplifi-rating')) customElements.define('emplifi-rating', EmplifiRating);
